import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { buildVendorParam, extractVendorId } from "../utils/vendorSlug";
import { vendorView } from "../utils/analytics";
import { SEOHead } from "../components/SEO/SEOHead";
import { shopPath } from "../utils/seoPaths";
import {
  buildVendorSeoProps,
  buildVendorShareText,
  buildProductShareText,
  coverDishImageOf,
  shareCardImageFor,
  withVendorCacheBuster,
} from "../components/SEO/vendorSeo";
import styled, { keyframes } from "styled-components";
import usePublishedHeight from "../hooks/usePublishedHeight";
import {
  SECTIONS_H_VAR,
  stickyUnderNav,
  sectionScrollMargin,
  heightUnderStack,
  scrollToSection,
} from "../utils/stickyStack";
import {
  moveCartToOutlet,
  setCurrentRestaurant,
  setRestaurantData,
} from "../redux/restaurantCartSlice";
import {
  fetchRestaurantDetails,
  fetchSingleRestaurant,
  clearRestaurantDetails,
  setSelectedRestaurant,
  applyMenuAvailabilityChange,
  applyBranchProductChange,
} from "../redux/restaurantSlice";
import useCatalogUpdates from "../hooks/useCatalogUpdates";
/* Branch operations (4 Oct 2026): each dish as it stands at this branch,
   "Get it from" another, live branch changes, one branch per cart and the
   cart check. */
import { branchProductChanged, cartLocationOf } from "../utils/branchCart";
import {
  addButtonLabel,
  blockedHere,
  blockedMessage,
  branchClosedNotice,
  locationKeyOf,
  outletIdOfKey,
  soldOutLabel,
} from "../utils/branchAvailability";
import BranchAvailabilityBadge from "../components/branch/BranchAvailabilityBadge";
import BranchCartReviewDialog from "../components/branch/BranchCartReviewDialog";
import { useCartBranchCheck } from "../hooks/useBranchCartCheck";
import useConfirmCartMove from "../hooks/useConfirmCartMove";
import OutletSwitchDialog from "../components/OutletSwitchDialog";
import { openNowOf } from "../utils/vendorOpenState";
import RestaurantCart from "../components/RestaurantCart";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ShareIcon from "@mui/icons-material/Share";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
// import IosShareIcon from "@mui/icons-material/IosShare";
import LinkIcon from "@mui/icons-material/Link";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import DownloadIcon from "@mui/icons-material/Download";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
// The restaurant placeholder Izi picked: fork, plate and knife on a plain
// near-white field. Used wherever a dish has no photograph yet.
import menuPlaceholder from "../assets/img/restaurant-dish-placeholder.png";
import { QRCodeCanvas } from "qrcode.react";
import {
  IconButton,
  Badge,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.bubble.css";
import restaurantBackgroundBanner from "../assets/vendorBackgroundBanner.png";
import RestaurantProductDialog from "./RestaurantProductDialog";
import RestaurantNavBar from "../components/RestaurantNavBar";
import Reviews from "../components/Reviews/Reviews";
import {
  getOptimizedUrl,
  optimizeCloudinaryUrl,
} from "../utils/cloudinaryOptimize";
import Stars from "../components/shop/Stars";
import { getVendorRatingSummary } from "../api/reviewsApi";
import VendorWhatsAppFloat from "../components/VendorWhatsAppFloat";
import ProductShareModal from "../components/ProductShare/ProductShareModal";
import { formatCurrency, SITE_ORIGIN } from "../config/market";
import {
  BRAND_GOLD,
  BRAND_INK,
  BRAND_INK_DEEP,
  BRAND_INK_LIFT,
} from "../config/brandColors";
import useCoverage from "../redux/useCoverage";
import { calculateDistance } from "../redux/userLocationSlice";
import { userRequest } from "../api/requestMethods";
import {
  sortLocationsForPicker,
  vendorLocationsOf,
  withOutletQuery,
} from "../utils/vendorOutlets";
import useDeliveryPoint from "../hooks/useDeliveryPoint";
import LocationPicker, { hereNameOf, locationName } from "../components/LocationPicker";
import CoverageBanner from "../components/CoverageBanner";
import LocationPrompt from "../components/LocationPrompt";
import useRestaurantAvailability from "../hooks/useRestaurantAvailability";
import DealTag from "../components/DealTag";
import PriceCut from "../components/PriceCut";
import { hasSalePrice } from "../utils/vendorDiscounts";
import RestaurantAvailabilityBanner from "../components/RestaurantAvailabilityBanner";
import CatalogueSearch from "../components/shop/CatalogueSearch";
import {
  filterSectionsByQuery,
  countProducts,
  normaliseQuery,
} from "../utils/menuSearch";
import {
  cardFooterRow,
  cardPriceRow,
  cardPriceOwnsItsRow,
  cardActionsRow,
} from "../components/productCardFooter";
import VendorTagline from "../components/VendorTagline";
import OrderTogetherEntry from "../components/GroupBasket/OrderTogetherEntry";

/* ── Shop banner delivery ────────────────────────────────────────────────
   The dashboard uploads this banner as a WIDE strip — 1440x175, about
   8.2:1 — and until now the page handed that raw URL straight to <img>.
   Two separate problems came out of that, and the three helpers below plus
   the `BannerPicture` / `RestaurantInfo` blocks are the fix for both.

   1. WEIGHT. A 1440px PNG was being delivered to a 320px phone. Every
      width here goes through Cloudinary, so f_auto swaps in WebP/AVIF and
      the phone downloads a phone-sized file.

   2. SHAPE. 8.2:1 is a desktop shape. A phone card is nowhere near that
      wide, so the strip has to be art-directed rather than merely resized
      — see MOBILE_BANNER_RATIO. */

const isCloudinary = (url) =>
  typeof url === "string" && url.includes("res.cloudinary.com");

/* Desktop keeps the strip's own geometry, so all we want is the right
   number of pixels. c_limit is deliberate: it scales DOWN to the requested
   width and leaves anything smaller alone, so a vendor who uploaded a
   narrower banner never gets it stretched. */
const DESKTOP_BANNER_WIDTHS = [720, 1080, 1440];

/* The mobile band is 5:1 rather than 8.2:1. The uploaded strips leave their
   left third close to empty on purpose — that is where the desktop overlay
   sits — so on a phone, where the overlay moves out from on top of the
   image, that third is dead space. c_fill,g_auto lets Cloudinary choose
   what survives instead of blind-cropping the centre: this restaurant keeps
   its food, and a vendor who centres a wordmark keeps the wordmark. */
const MOBILE_BANNER_RATIO = 5;
const MOBILE_BANNER_WIDTHS = [760, 1100];

const bannerSrcSet = (url, widths, transform) =>
  isCloudinary(url)
    ? widths
        .map((w) => `${optimizeCloudinaryUrl(url, transform(w))} ${w}w`)
        .join(", ")
    : undefined;

const desktopBannerSrcSet = (url) =>
  bannerSrcSet(url, DESKTOP_BANNER_WIDTHS, (w) => ({
    width: w,
    crop: "limit",
    quality: "auto:good",
  }));

const mobileBannerSrcSet = (url) =>
  bannerSrcSet(url, MOBILE_BANNER_WIDTHS, (w) => ({
    width: w,
    height: Math.round(w / MOBILE_BANNER_RATIO),
    crop: "fill",
    gravity: "auto",
    quality: "auto:good",
  }));

const RestaurantShopPage = () => {
  const { id: idParam } = useParams();
  const id = extractVendorId(idParam);
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  // Get outletId from query params
  const searchParams = new URLSearchParams(location.search);
  const outletId = searchParams.get("outletId");

  const restaurantDetails = useSelector((state) => state.restaurants.details);
  const selectedActiveRestaurant = useSelector(
    (state) => state.restaurants.selectedRestaurant,
  );
  const isFetching = useSelector((state) => state.restaurants.isFetching);
  /* Read the cart under the key items are actually WRITTEN to.

     The dialog keys every add on `restaurantCart.currentRestaurant`, and
     `setRestaurantData` overwrites that with `restaurant.owner`. This page was
     reading `carts[id]`, the id from the URL. Those agree only when the URL
     already carries the owner id — arrive from search, which links by
     `Restaurant._id`, and adds land in one bucket while the badge counts
     another, so the cart reads as empty no matter how many dishes are added
     and nothing tells the customer otherwise. */
  const cartKey = useSelector(
    (state) => state.restaurantCart.currentRestaurant
  ) || id;
  const cart = useSelector(
    (state) => state.restaurantCart.carts[cartKey] || {}
  );
  const cartCount = Object.values(cart).reduce(
    (total, item) => total + item.quantity,
    0,
  );
  /* The cart check at its branch (4 Oct 2026): quiet while browsing, and the
     "Changes at <branch>" review right after the cart moves to another
     branch of this restaurant. */
  const { reviewNextCheck, dialogProps: branchDialogProps } = useCartBranchCheck({
    vertical: "restaurant",
    cartId: cartKey,
    checkWhen: Boolean(restaurantDetails),
    silent: true,
  });
  // Authoritative delivery coverage for THIS restaurant vs the customer's set
  // location (backend GET /api/coverage — same decision as the order-create
  // gate). Powers the shop-entry banner + the add-to-cart hard-block so the
  // customer learns "can't deliver here" now, not at the final Confirm step.
  // A branch is checked against ITS coordinates and radius, not the parent's.
  const coverage = useCoverage({ vendorId: id, outletId, vendorType: "restaurant" });
  const availability = useRestaurantAvailability({
    vendorId: id,
    outletId,
  });
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);

  const sectionsNavRef = useRef(null);
  /* The strip's rendered height, for the scroll margins and the inner
     scroller's height (utils/stickyStack). */
  usePublishedHeight(sectionsNavRef, SECTIONS_H_VAR);
  const qrCodeRef = useRef(null);
  const [activeSection, setActiveSection] = useState(null);
  // Menu search. The sections the page renders are ALWAYS `visibleSections`
  // (live products, narrowed by the query); the unfiltered enabled count only
  // decides whether there is a menu at all, so "no dish matches" and "no menu"
  // stay two different messages.
  const [menuQuery, setMenuQuery] = useState("");
  const liveSections = useMemo(
    () => filterSectionsByQuery(restaurantDetails?.sections, ""),
    [restaurantDetails?.sections],
  );
  const visibleSections = useMemo(
    () => filterSectionsByQuery(restaurantDetails?.sections, menuQuery),
    [restaurantDetails?.sections, menuQuery],
  );
  const hasMenu = liveSections.length > 0;
  const isSearching = normaliseQuery(menuQuery).length > 0;
  const visibleDishCount = countProducts(visibleSections);
  const [isCartVisible, setIsCartVisible] = useState(false);
  /* "Order again" lands here with the rebuilt cart to review
     (utils/reorderIntoCart), so the cart opens on arrival. Read once: the
     reset below runs on mount and must not close it again. */
  const openCartOnArrivalRef = useRef(Boolean(location.state?.openCart));
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loadedImages, setLoadedImages] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);

  /* Rolled-up store rating. This block replaces a literal "⭐⭐⭐⭐" that was
     rendered on EVERY restaurant regardless of its reviews — the same four
     stars for a kitchen with two hundred and a kitchen with none. The
     backend already names that exact bug in the comment on
     getVendorRatingSummaryBatch; the mobile Retailers list was fixed, this
     page was not. Same endpoint and same treatment as VendorShopPage:
     GET /api/reviews/vendor-summary is the single source of truth, and it
     folds reviews written against the restaurant together with reviews
     across its dishes, because shoppers rate dishes, not entities. */
  const [restaurantRating, setRestaurantRating] = useState(null);
  const ratingSubjectId = selectedActiveRestaurant?._id || id;

  useEffect(() => {
    if (!ratingSubjectId) return;
    let active = true;
    getVendorRatingSummary({
      subjectType: "vendor_restaurant",
      subjectId: ratingSubjectId,
    })
      .then((res) => {
        const stats = res?.data?.data?.stats;
        if (active && stats) {
          setRestaurantRating({
            avg: Number(stats.averageRating) || 0,
            count: Number(stats.totalReviews) || 0,
          });
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [ratingSubjectId]);
  const [qrCodeOpen, setQrCodeOpen] = useState(false);
  // Visual share poster modal (branded poster preview + share actions).
  const [shareProduct, setShareProduct] = useState(null);
  const [shareProductUrl, setShareProductUrl] = useState("");

  // Durable, URL-independent vendor attribution (GA4 vendor_view). Fires once
  // per restaurant, after the record loads so the model _id is known.
  const vendorViewedRef = useRef(null);
  useEffect(() => {
    const vid = restaurantDetails?._id || selectedActiveRestaurant?._id;
    if (!vid || vendorViewedRef.current === String(vid)) return;
    vendorViewedRef.current = String(vid);
    vendorView({
      vendorId: vid,
      vendorType: "restaurant",
      path: location.pathname,
    });
  }, [
    restaurantDetails?._id,
    selectedActiveRestaurant?._id,
    location.pathname,
  ]);

  // Clear previous restaurant data when id changes
  useEffect(() => {
    // Clear restaurant details and selected restaurant when navigating to a new restaurant
    dispatch(clearRestaurantDetails());
    dispatch(setSelectedRestaurant(null));
    // Reset local state
    setActiveSection(null);
    setSelectedProduct(null);
    setIsCartVisible(openCartOnArrivalRef.current);
    openCartOnArrivalRef.current = false;
  }, [id, dispatch]);

  // The menu at the branch this storefront is open on (4 Oct 2026): every
  // dish as it stands there, and a curated branch menu's own list. An empty
  // branch menu still shows the whole menu, as before.
  useEffect(() => {
    if (id) {
      dispatch(fetchRestaurantDetails({ restaurantId: id, outletId }));
      dispatch(setCurrentRestaurant(id));
    }
  }, [dispatch, id, outletId]);

  /* The kitchen on screen just changed its opening hours. Refresh THIS
     storefront's availability and nothing else: the banner, the dish badges and
     the add-to-cart gate all read it, and a customer sitting on a menu should
     not have to reload to discover the kitchen has closed. One vendor's edit
     costs one request from the customers actually looking at that vendor.

     Held in a ref because `availability.refresh` changes identity between
     renders. Putting it in the socket effect's dependency array below would
     tear the websocket down and rebuild it on every render. */
  const hoursChangedRef = useRef(() => {});
  // A branch of this restaurant was switched on or off. Set further down,
  // where the branch rows live.
  const branchesChangedRef = useRef(() => {});
  useEffect(() => {
    hoursChangedRef.current = (payload = {}) => {
      const changed = String(payload.restaurantId || "");
      if (!changed) return;
      /* The URL id may be the restaurant's OWN id or its owner's, and unlike
         the visibility payload this one carries no ownerId to match on. So
         match the loaded restaurant's real id too, which is resolved by then
         whichever way the customer arrived. `parentRestaurantId` covers the
         payload the server sends per outlet inheriting its parent's hours. */
      const realId = String(selectedActiveRestaurant?._id || "");
      const parent = String(payload.parentRestaurantId || "");
      const mine =
        changed === String(id) ||
        changed === String(outletId || "") ||
        (realId && changed === realId) ||
        parent === String(id) ||
        (realId && parent === realId);
      if (mine) availability.refresh({ silent: true });
    };
  });

  // Sold out for the day, live. Same room and lifecycle as VendorShopPage's
  // listener; the payload names the restaurant by both its id and its owner
  // id, and the URL id may be either, so accept both.
  useCatalogUpdates(
    {
      "product:availabilityChanged": (payload) => {
        if (payload.source !== "restaurant") return;
        const mine =
          String(payload.restaurantId || "") === String(id) ||
          String(payload.ownerId || "") === String(id);
        if (mine) dispatch(applyMenuAvailabilityChange(payload));
      },
      // The kitchen you are looking at was just unpublished or suspended.
      // Leaving the menu on screen would let someone keep building a cart they
      // can no longer check out with (the server refuses the order too, but
      // finding out at the payment step is a worse way to learn it).
      "restaurant:visibilityChanged": (payload) => {
        const mine =
          String(payload.restaurantId || "") === String(id) ||
          String(payload.ownerId || "") === String(id);
        if (mine && payload.visible === false) {
          history.push("/restaurants");
        }
      },
      restaurantHoursUpdated: (payload) => {
        hoursChangedRef.current(payload);
      },
      outletStatusChanged: (payload) => {
        if (payload.parentType && payload.parentType !== "restaurant") return;
        branchesChangedRef.current(payload);
      },
      /* A branch switched a dish on or off, or sold it out (4 Oct 2026).
         The open menu flips in place (the slice ignores other restaurants),
         and any cart line it touches is checked again before paying. */
      "branchProduct:changed": (payload) => {
        dispatch(branchProductChanged(payload));
        if (payload.vertical && payload.vertical !== "restaurant") return;
        dispatch(applyBranchProductChange(payload));
      },
    },
    { enabled: Boolean(id) },
  );

  // Fetch restaurant metadata if not already loaded
  useEffect(() => {
    // Only fetch if we don't have restaurant data OR if the selected restaurant doesn't match current id
    // Use AND: re-fetch only when NEITHER _id NOR owner matches the URL id
    const matchesById =
      selectedActiveRestaurant?._id?.toString() === id ||
      selectedActiveRestaurant?._id?.toString() === idParam;
    const matchesByOwner =
      selectedActiveRestaurant?.owner?.toString() === id ||
      selectedActiveRestaurant?.owner?.toString() === idParam;
    const shouldFetch =
      !selectedActiveRestaurant || (!matchesById && !matchesByOwner);

    if (shouldFetch && id) {
      dispatch(fetchSingleRestaurant(id))
        .unwrap()
        .then((restaurant) => {
          dispatch(setRestaurantData(restaurant));
        })
        .catch((error) => {
          console.error("Failed to fetch restaurant:", error);
          // Only redirect if fetch fails
          history.push("/");
        });
    } else if (selectedActiveRestaurant) {
      // Store restaurant data in cart state for display
      dispatch(setRestaurantData(selectedActiveRestaurant));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedActiveRestaurant, id, idParam, history, dispatch]);

  /* Every place this restaurant can be ordered from (its main branch and
     each active outlet, each with its distance from the shopper), for the
     location picker under the banner (4 Oct 2026). The Restaurants page
     opened this storefront on the branch that serves the shopper best; the
     picker shows which and lets them choose another.

     The rows are the listing's own (GET /restaurants, each branch with its
     server-computed open state), read from the store when the shopper came
     from the list. A shared link lands here cold, so the same row is then
     asked for once by id, and only when the profile says the kitchen has
     branches at all: a one-location restaurant costs nothing. The request
     stays local rather than going through the restaurants slice, whose one
     isFetching flag would put this page back on its skeleton. */
  const listingRows = useSelector((state) => state.restaurants.restaurantData);
  const [fetchedRestaurantRow, setFetchedRestaurantRow] = useState(null);
  const restaurantIds = useMemo(
    () =>
      new Set(
        [id, selectedActiveRestaurant?._id, selectedActiveRestaurant?.owner]
          .filter(Boolean)
          .map(String),
      ),
    [id, selectedActiveRestaurant?._id, selectedActiveRestaurant?.owner],
  );
  const restaurantRow = useMemo(() => {
    const isThisRestaurant = (row) =>
      Boolean(row) &&
      (restaurantIds.has(String(row._id)) || restaurantIds.has(String(row.owner)));
    // A row fetched here is the newer one: it is only ever asked for when
    // the listing has none, or when a branch has just been switched.
    return (
      (isThisRestaurant(fetchedRestaurantRow) ? fetchedRestaurantRow : null) ||
      (Array.isArray(listingRows) ? listingRows.find(isThisRestaurant) : null)
    );
  }, [listingRows, fetchedRestaurantRow, restaurantIds]);
  const fetchRestaurantRow = useCallback(
    () =>
      userRequest
        .get("/restaurants", { params: { vendorId: id } })
        .then((res) => res?.data?.restaurants?.[0] || null),
    [id],
  );
  const hasBranches =
    Array.isArray(selectedActiveRestaurant?.outlets) &&
    selectedActiveRestaurant.outlets.length > 0;
  useEffect(() => {
    if (restaurantRow || !hasBranches || !id) return undefined;
    let active = true;
    fetchRestaurantRow()
      .then((row) => {
        if (active && row) setFetchedRestaurantRow(row);
      })
      // The picker is extra: without the row the page simply has no bar.
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [id, hasBranches, restaurantRow, fetchRestaurantRow]);
  // One of this restaurant's branches was switched on or off: fetch the row
  // again so the location picker drops or regains it while the shopper is on
  // the page, open or not (4 Oct 2026). Local, like the request above, so the
  // page keeps its content.
  useEffect(() => {
    branchesChangedRef.current = (payload = {}) => {
      if (!restaurantIds.has(String(payload.restaurantId || ""))) return;
      fetchRestaurantRow()
        .then((row) => {
          if (row) setFetchedRestaurantRow(row);
        })
        .catch(() => {});
    };
  });
  const deliveryPoint = useDeliveryPoint();
  const restaurantLocations = useMemo(
    () =>
      vendorLocationsOf(restaurantRow, { deliveryPoint, calculateDistance }),
    [restaurantRow, deliveryPoint],
  );
  /* Open this restaurant on another of its branches (or its main branch),
     keeping the slug the page has already canonicalised to. ONE BRANCH PER
     CART (4 Oct 2026): the cart moves along and every line is checked there,
     with "Changes at <branch>" before the shopper pays. It used to stay
     behind, which let a basket mix two kitchens. The picker asks first when
     the cart is on another branch (`cartBoundElsewhere`), and so does "Get it
     from" on a dish (`getItFrom`, shopper journey audit 4 Oct 2026). */
  const cartLocation = cartLocationOf(cart);
  const cartBoundElsewhere = useCallback(
    (picked) => cartCount > 0 && cartLocation !== locationKeyOf(picked),
    [cartCount, cartLocation],
  );
  const chooseLocation = useCallback(
    (picked) => {
      const target = picked?.isOutlet ? String(picked._id) : null;
      if (cartCount > 0 && cartLocation !== locationKeyOf(picked)) {
        dispatch(moveCartToOutlet({ restaurantId: cartKey, outletId: target }));
        reviewNextCheck();
      }
      history.push(withOutletQuery(location.pathname, target));
    },
    [cartCount, cartLocation, cartKey, dispatch, history, location.pathname, reviewNextCheck],
  );
  const getItFrom = useConfirmCartMove({
    needsAsk: cartBoundElsewhere,
    onMove: chooseLocation,
  });
  /* The branch this menu was read at, and the one the cart is on, by name,
     as the apps name them: a branch by its own name, the main branch as
     "<restaurant> (main branch)", and a one-kitchen restaurant by its own
     name alone. */
  const restaurantName = selectedActiveRestaurant?.name || restaurantRow?.name || "";
  const nameOfLocation = useCallback(
    (key) => {
      const outlet = outletIdOfKey(key);
      const row = outlet
        ? restaurantLocations.find((r) => r.isOutlet && String(r._id) === outlet)
        : null;
      if (outlet && !row) return restaurantName;
      return hereNameOf(restaurantLocations, row, restaurantName);
    },
    [restaurantLocations, restaurantName],
  );
  const hereName = nameOfLocation(outletId);
  /* The branch words are for a restaurant WITH branches. A one-kitchen
     restaurant keeps exactly the words it had: the server marks such a dish
     soldOutToday too, so "Sold out today" and "back on the menu tomorrow"
     still say it (shopper journey audit, 4 Oct 2026). */
  const branchWords = restaurantLocations.length > 1;

  /* The branch in the URL stopped taking orders while the page was open:
     the row refresh after the switch-off drops it from the picker. The
     server would refuse an order naming it, so the page opens the best
     branch left, with the cart when it was on that branch, and says why, as
     the brand storefront and the apps do. Only a branch this page has seen
     in the row counts, so a link opened before the row loads moves nobody.
     Once per branch. */
  const seenHereRef = useRef(null);
  const movedOffRef = useRef(null);
  useEffect(() => {
    if (!outletId) return;
    const here = restaurantLocations.find(
      (row) => row.isOutlet && String(row._id) === String(outletId),
    );
    if (here) {
      seenHereRef.current = { id: String(outletId), name: here.name || "" };
      return;
    }
    if (seenHereRef.current?.id !== String(outletId)) return;
    if (movedOffRef.current === String(outletId)) return;
    const best = sortLocationsForPicker(restaurantLocations, { isOpen: openNowOf })[0];
    if (!best) return;
    movedOffRef.current = String(outletId);
    const target = best.isOutlet ? String(best._id) : null;
    const cartMoved = cartCount > 0 && cartLocation === locationKeyOf(outletId);
    if (cartMoved) {
      dispatch(moveCartToOutlet({ restaurantId: cartKey, outletId: target }));
      reviewNextCheck();
    }
    const notice = branchClosedNotice({
      closed: seenHereRef.current.name,
      next: locationName(best, restaurantName),
      cartMoved,
    });
    toast.info(`${notice.title}. ${notice.message}`);
    history.replace(withOutletQuery(location.pathname, target));
  }, [
    outletId,
    restaurantLocations,
    cartCount,
    cartLocation,
    cartKey,
    dispatch,
    reviewNextCheck,
    restaurantName,
    history,
    location.pathname,
  ]);

  useEffect(() => {
    const name = selectedActiveRestaurant?.name || restaurantDetails?.name;
    if (!name) return;
    const canonical = buildVendorParam(name, id);
    const prefix = outletId
      ? `/restaurant/shop/${canonical}?outletId=${outletId}`
      : `/restaurant/shop/${canonical}`;
    if (idParam !== canonical) {
      history.replace(prefix);
    }
  }, [
    selectedActiveRestaurant?.name,
    restaurantDetails?.name,
    id,
    idParam,
    outletId,
    history,
  ]);

  useEffect(() => {
    if (
      restaurantDetails &&
      restaurantDetails.sections &&
      restaurantDetails.sections.length > 0
    ) {
      const firstSectionWithProducts = restaurantDetails.sections.find(
        (section) => section.products && section.products.length > 0,
      );
      if (firstSectionWithProducts) {
        setActiveSection(`section-${firstSectionWithProducts._id}`);
        setTimeout(() => {
          const sectionElement = document.getElementById(
            `section-${firstSectionWithProducts._id}`,
          );
          if (sectionElement) {
            // Measured bars, not a viewport percentage; see utils/stickyStack.
            scrollToSection(
              sectionElement,
              document.querySelector("[data-sections-container]"),
              { pinStack: false }
            );
          }
        }, 100);
      }
    }
  }, [restaurantDetails]);

  // Scroll-spy: track which section is active based on SectionsContainer scroll position
  useEffect(() => {
    const container = document.querySelector("[data-sections-container]");
    if (!container) return;

    // Spy on what is rendered: a search narrows the sections, and the nav
    // must highlight one that is on screen, not one the query removed.
    const sectionsWithProducts = visibleSections;

    if (sectionsWithProducts.length === 0) return;

    const handleScroll = () => {
      const containerTop = container.getBoundingClientRect().top;
      // Trigger line at 35% down from the container's visible top edge
      // — section header activates as soon as it reaches this line
      const triggerLine = containerTop + container.clientHeight * 0.35;

      let currentActiveId = null;

      for (const section of sectionsWithProducts) {
        const element = document.getElementById(`section-${section._id}`);
        if (!element) continue;
        // If this section's header is at or above the trigger line, it's active
        if (element.getBoundingClientRect().top <= triggerLine) {
          currentActiveId = `section-${section._id}`;
        }
      }

      // Default to the first section if nothing has scrolled past yet
      if (!currentActiveId && sectionsWithProducts.length > 0) {
        currentActiveId = `section-${sectionsWithProducts[0]._id}`;
      }

      if (currentActiveId) {
        setActiveSection(currentActiveId);
      }
    };

    // Run once immediately to set initial state
    handleScroll();

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [visibleSections]);

  // Auto-scroll the active section tab into view in the horizontal nav
  useEffect(() => {
    if (activeSection && sectionsNavRef.current) {
      const activeTab = sectionsNavRef.current.querySelector(
        `a[href="#${activeSection}"]`,
      );
      if (activeTab) {
        activeTab.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    }
  }, [activeSection]);

  const handleSectionClick = (sectionId) => (event) => {
    event.preventDefault();
    const sectionElement = document.getElementById(sectionId);
    const container =
      sectionElement?.closest('[style*="overflow"]') ||
      document.querySelector("[data-sections-container]");

    if (sectionElement && container) {
      scrollToSection(sectionElement, container);
      setActiveSection(sectionId);
    } else if (sectionElement) {
      sectionElement.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveSection(sectionId);
    }
  };

  const getLinkClasses = (sectionId) => {
    return activeSection === sectionId ? "active-section" : "";
  };

  const handleProductClick = (product) => {
    // Deliberately NOT gated on availability/coverage. A closed restaurant or
    // an out-of-range address must not make the menu feel broken: the tap
    // always opens the item, and the dialog explains why ordering is blocked
    // and disables its own CTA. Swallowing the tap behind a toast left people
    // clicking a dead card with no idea why nothing happened.
    // The real order gate lives in the dialog, the cart and order-create.
    // Keep the outlet identity on the cart line. The URL query is otherwise
    // lost as soon as the shopper leaves the storefront, which could make the
    // checkout validate the parent restaurant's hours instead of the selected
    // outlet's custom hours.
    setSelectedProduct({
      ...product,
      ...(outletId ? { outletId } : {}),
    });
  };

  const handleProductShare = (e, product) => {
    e.stopPropagation();
    // Open the visual share modal (branded poster preview + share actions)
    // instead of dumping plain text into the OS share sheet. WhatsApp Status
    // and chats are visual-first, so a poster converts far better than a link.
    const shopUrl = getRestaurantUrl();
    setShareProductUrl(shopUrl);
    setShareProduct({
      ...product,
      vendorName: selectedActiveRestaurant?.name,
    });
  };

  const handleProductWhatsAppShare = (e, product) => {
    e.stopPropagation();
    const shopUrl = getRestaurantUrl();
    const text = buildProductShareText(product, {
      url: shopUrl,
      vendor: selectedActiveRestaurant,
    });
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const handleCloseDialog = () => {
    setSelectedProduct(null);
  };

  const handleImageLoaded = useCallback((productId) => {
    setLoadedImages((prev) => ({ ...prev, [productId]: true }));
  }, []);

  const handleShareClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseShareMenu = () => {
    setAnchorEl(null);
  };

  const handleShowQRCode = () => {
    setAnchorEl(null);
    setQrCodeOpen(true);
  };

  const handleCloseQRCode = () => {
    setQrCodeOpen(false);
  };

  const getRestaurantUrl = () => {
    const baseUrl = SITE_ORIGIN;
    const slugParam = buildVendorParam(selectedActiveRestaurant?.name, id);
    const path = outletId
      ? `${baseUrl}/restaurant/shop/${slugParam}?outletId=${outletId}`
      : `${baseUrl}/restaurant/shop/${slugParam}`;
    return withVendorCacheBuster(path, selectedActiveRestaurant);
  };

  const handleDownloadQRCode = () => {
    if (!qrCodeRef.current) return;

    const canvas = qrCodeRef.current.querySelector("canvas");
    if (!canvas) return;

    // Convert canvas to blob
    canvas.toBlob((blob) => {
      if (!blob) {
        toast.error("Failed to generate QR code image");
        return;
      }

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${
        selectedActiveRestaurant?.name || "restaurant"
      }-qr-code.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("QR code downloaded successfully!");
    }, "image/png");
  };

  const handleShareQRCode = () => {
    if (!qrCodeRef.current) return;

    const canvas = qrCodeRef.current.querySelector("canvas");
    if (!canvas) return;

    // Convert canvas to blob
    canvas.toBlob((blob) => {
      if (!blob) {
        toast.error("Failed to generate QR code image");
        return;
      }

      const restaurantName = selectedActiveRestaurant?.name || "Restaurant";
      const shareText = `Check out ${restaurantName} on Lusuku! Scan the QR code to view the menu and order.`;
      const shareUrl = getRestaurantUrl();

      // Check if Web Share API is available (mobile devices)
      if (navigator.share && navigator.canShare) {
        // Convert blob to File for sharing
        const file = new File([blob], `${restaurantName}-qr-code.png`, {
          type: "image/png",
        });

        if (navigator.canShare({ files: [file] })) {
          navigator
            .share({
              title: `${restaurantName} QR Menu`,
              text: shareText,
              url: shareUrl,
              files: [file],
            })
            .then(() => {
              toast.success("QR code shared successfully!");
            })
            .catch((error) => {
              if (error.name !== "AbortError") {
                console.error("Error sharing:", error);
                // Fallback to WhatsApp
                handleShareViaWhatsApp(shareText, shareUrl);
              }
            });
        } else {
          // Fallback to WhatsApp if file sharing not supported
          handleShareViaWhatsApp(shareText, shareUrl);
        }
      } else {
        // Fallback to WhatsApp for desktop
        handleShareViaWhatsApp(shareText, shareUrl);
      }
    }, "image/png");
  };

  const handleShareViaWhatsApp = (text, url) => {
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(
      `${text}\n\n${url}`,
    )}`;
    window.open(whatsappUrl, "_blank");
    toast.success("Opening WhatsApp...");
  };

  /* Featured menu items (max 5 enabled) shared via copy-link / WhatsApp.
     headerLabel ('Menu') keeps the restaurant share reading like a menu
     rather than a generic catalogue. The bullet is left to vendorSeo: the
     payload must stay emoji-free (see the note there). */
  const buildMenuShareText = () => {
    if (
      !isRestaurantDataForCurrentId ||
      !selectedActiveRestaurant ||
      !restaurantDetails ||
      !isDataForCurrentRestaurant
    ) {
      return null;
    }
    const products = restaurantDetails.sections
      .flatMap((s) => s.products?.filter((p) => p.enabled) || [])
      .slice(0, 5);
    return buildVendorShareText(selectedActiveRestaurant, {
      url: withVendorCacheBuster(
        `${SITE_ORIGIN}/restaurant/shop/${buildVendorParam(selectedActiveRestaurant?.name, id)}`,
        selectedActiveRestaurant,
      ),
      products,
      headerLabel: "Menu",
    });
  };

  /* What the share menu shows at the top: resolved by the same rule the
     crawler's card follows (see shareCardImageFor). */
  const shareCardPreview = shareCardImageFor(
    selectedActiveRestaurant,
    coverDishImageOf(selectedActiveRestaurant, restaurantDetails?.sections)
  );

  const handleCopyLink = () => {
    const text = buildMenuShareText();
    if (!text) return;
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success("Restaurant details copied to clipboard!"))
      .catch((err) => {
        console.error("Failed to copy: ", err);
        toast.error("Failed to copy to clipboard");
      });
    setAnchorEl(null);
  };

  const handleWhatsAppShare = () => {
    const text = buildMenuShareText();
    if (!text) return;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    setAnchorEl(null);
  };

  // Only show data if it matches the current id
  const isDataForCurrentRestaurant =
    restaurantDetails &&
    restaurantDetails.restaurantId &&
    restaurantDetails.restaurantId.toString() === id;

  const isRestaurantDataForCurrentId =
    selectedActiveRestaurant &&
    (selectedActiveRestaurant.owner?.toString() === id ||
      selectedActiveRestaurant._id?.toString() === id);

  // Show skeleton loading if fetching, no details, or details don't match current id
  if (isFetching || !restaurantDetails || !isDataForCurrentRestaurant) {
    return (
      <Container>
        <RestaurantNavBar setIsCartVisible={setIsCartVisible} />
        <SkeletonContainer>
          {/* Banner — matches RestaurantInfo: full-width image with gradient overlay, avatar+text at bottom */}
          <SkeletonBanner>
            <SkeletonBannerOverlay>
              <SkeletonAvatarBox />
              <SkeletonBannerText>
                <SkeletonLine width="55%" height="24px" />
                <SkeletonLine
                  width="35%"
                  height="14px"
                  style={{ marginTop: "8px" }}
                />
                <SkeletonLine
                  width="80px"
                  height="12px"
                  style={{ marginTop: "6px" }}
                />
              </SkeletonBannerText>
              <SkeletonShareBtn />
            </SkeletonBannerOverlay>
          </SkeletonBanner>

          {/* Section Nav — matches SectionsNav: space-around, rounded top, bottom border */}
          <SkeletonNav>
            {[1, 2, 3, 4, 5].map((i) => (
              <SkeletonNavItem
                key={i}
                width={i === 1 ? "90px" : `${60 + i * 12}px`}
                isFirst={i === 1}
              />
            ))}
          </SkeletonNav>

          {/* First section — matches SectionHeader + ProductsGrid */}
          <SkeletonSectionTitle>
            <SkeletonLine
              width="160px"
              height="20px"
              style={{
                background:
                  "linear-gradient(90deg, #e0e0e0 25%, #d0d0d0 37%, #e0e0e0 63%)",
                backgroundSize: "800px 100%",
              }}
            />
            <SkeletonDot />
            <SkeletonLine
              width="200px"
              height="12px"
              style={{
                marginLeft: "16px",
                background:
                  "linear-gradient(90deg, #e8e8e8 25%, #ddd 37%, #e8e8e8 63%)",
                backgroundSize: "800px 100%",
              }}
            />
          </SkeletonSectionTitle>

          <SkeletonGrid>
            {[1, 2, 3, 4].map((i) => (
              <SkeletonCard key={i}>
                <SkeletonImage />
                <SkeletonCardBody>
                  <SkeletonLine width="80%" height="14px" />
                  <SkeletonLine
                    width="100%"
                    height="10px"
                    style={{ marginTop: "8px" }}
                  />
                  <SkeletonLine
                    width="60%"
                    height="10px"
                    style={{ marginTop: "4px" }}
                  />
                  <SkeletonPriceRow>
                    <SkeletonLine width="70px" height="14px" />
                    <SkeletonCircle />
                  </SkeletonPriceRow>
                </SkeletonCardBody>
              </SkeletonCard>
            ))}
          </SkeletonGrid>

          {/* Second section skeleton */}
          <SkeletonSectionTitle>
            <SkeletonLine
              width="130px"
              height="20px"
              style={{
                background:
                  "linear-gradient(90deg, #e0e0e0 25%, #d0d0d0 37%, #e0e0e0 63%)",
                backgroundSize: "800px 100%",
              }}
            />
            <SkeletonDot />
          </SkeletonSectionTitle>

          <SkeletonGrid>
            {[1, 2, 3, 4].map((i) => (
              <SkeletonCard key={`s2-${i}`}>
                <SkeletonImage />
                <SkeletonCardBody>
                  <SkeletonLine width="75%" height="14px" />
                  <SkeletonLine
                    width="90%"
                    height="10px"
                    style={{ marginTop: "8px" }}
                  />
                  <SkeletonLine
                    width="50%"
                    height="10px"
                    style={{ marginTop: "4px" }}
                  />
                  <SkeletonPriceRow>
                    <SkeletonLine width="70px" height="14px" />
                    <SkeletonCircle />
                  </SkeletonPriceRow>
                </SkeletonCardBody>
              </SkeletonCard>
            ))}
          </SkeletonGrid>
        </SkeletonContainer>
      </Container>
    );
  }

  // Truncate the description if it's too long to 20 words
  const truncateDescription = (description) => {
    const words = description.split(" ");
    return words.length > 20
      ? words.slice(0, 20).join(" ") + "..."
      : description;
  };

  const bannerUrl =
    (isRestaurantDataForCurrentId &&
      (selectedActiveRestaurant?.shopBannerUrl ||
        selectedActiveRestaurant?.bannerUrl)) ||
    restaurantBackgroundBanner;

  return (
    <Container>
      {/* Vendor-first social card: leads with the restaurant's banner / logo
          and emits an Organization JSON-LD with the restaurant as the org.
          Falls back to Lusuku defaults if no branding has been uploaded. */}
      <SEOHead
        {...buildVendorSeoProps(selectedActiveRestaurant, {
          /* One address per restaurant whichever link brought the visitor
             (owner id or restaurant id), and the same one the sitemap lists
             (src/utils/seoPaths.js). */
          canonicalPath:
            shopPath("restaurant", selectedActiveRestaurant) ||
            `/restaurant/shop/${buildVendorParam(selectedActiveRestaurant?.name, id)}`,
          vendorType: "restaurant",
        })}
      />
      <RestaurantNavBar setIsCartVisible={setIsCartVisible} />
      {restaurantDetails && isDataForCurrentRestaurant && (
        <>
          <RestaurantInfo>
            {/* <picture> rather than a bare srcSet because the two
                breakpoints need different CROPS, not just different sizes.
                srcSet/sizes can only pick a resolution; art direction needs
                a <source>. The mobile <source> is skipped when the vendor
                has no banner and we fall back to the bundled asset, which
                Cloudinary cannot transform. */}
            <BannerPicture>
              {mobileBannerSrcSet(bannerUrl) && (
                <source
                  media="(max-width: 768px)"
                  srcSet={mobileBannerSrcSet(bannerUrl)}
                  sizes="100vw"
                />
              )}
              <RestaurantBannerImage
                src={bannerUrl}
                srcSet={desktopBannerSrcSet(bannerUrl)}
                sizes="(max-width: 768px) 100vw, min(100vw, 1440px)"
                alt={
                  isRestaurantDataForCurrentId
                    ? `${selectedActiveRestaurant?.name || "Restaurant"} banner`
                    : "Restaurant Banner"
                }
              />
            </BannerPicture>
            <RestaurantInfoOverlay>
              <AvatarWrapper>
                {isRestaurantDataForCurrentId &&
                selectedActiveRestaurant?.imageUrl ? (
                  <StyledAvatar
                    alt={selectedActiveRestaurant?.name || "Restaurant"}
                    src={getOptimizedUrl(
                      selectedActiveRestaurant.imageUrl,
                      "vendorLogo",
                    )}
                  />
                ) : (
                  /* No logo uploaded: a branded tile rather than MUI's grey
                     person silhouette, which now fills the whole badge. */
                  <AvatarPlaceholder>
                    <RestaurantMenuIcon />
                  </AvatarPlaceholder>
                )}
              </AvatarWrapper>
              <RestaurantText>
                <h1>
                  {isRestaurantDataForCurrentId
                    ? selectedActiveRestaurant?.name
                    : "Loading..."}
                </h1>
                {isRestaurantDataForCurrentId && (
                  <HeaderTagline vendor={selectedActiveRestaurant} />
                )}
                <p>
                  {isRestaurantDataForCurrentId
                    ? selectedActiveRestaurant?.address
                    : ""}
                </p>
                {restaurantRating && restaurantRating.count > 0 ? (
                  <p
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <Stars value={restaurantRating.avg} size={16} />
                    <span style={{ fontSize: 13 }}>
                      {restaurantRating.avg.toFixed(1)} (
                      {restaurantRating.count} review
                      {restaurantRating.count !== 1 ? "s" : ""})
                    </span>
                  </p>
                ) : (
                  <p style={{ fontSize: 13, opacity: 0.85 }}>No ratings yet</p>
                )}
              </RestaurantText>
              <ShareButton onClick={handleShareClick}>
                <ShareIcon />
              </ShareButton>
            </RestaurantInfoOverlay>
            <StyledMenu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleCloseShareMenu}
            >
              {/* The thumbnail is the card the recipient will get: same
                  picture, same 1200x630 crop as the crawler builds, so a
                  vendor sees what travels before sending. It used to draw
                  the logo, which stopped being the card on 26 Sep 2026. */}
              {isRestaurantDataForCurrentId && shareCardPreview.image && (
                <MenuItem disabled style={{ opacity: 1 }}>
                  <img
                    src={getOptimizedUrl(
                      shareCardPreview.image,
                      shareCardPreview.fit === "cover"
                        ? "ogShareCardCover"
                        : "ogShareCard"
                    )}
                    alt={selectedActiveRestaurant.name}
                    style={{
                      width: "100%",
                      aspectRatio: "1200 / 630",
                      objectFit: "cover",
                      borderRadius: "8px",
                      display: "block",
                    }}
                  />
                </MenuItem>
              )}
              <MenuItem onClick={handleCopyLink}>
                <ListItemIcon>
                  <LinkIcon fontSize="small" style={{ color: "#1976d2" }} />
                </ListItemIcon>
                <ListItemText primary="Copy Shop Link" />
              </MenuItem>
              <MenuItem onClick={handleWhatsAppShare}>
                <ListItemIcon>
                  <WhatsAppIcon fontSize="small" style={{ color: "#25D366" }} />
                </ListItemIcon>
                <ListItemText primary="Share on WhatsApp" />
              </MenuItem>
              <MenuItem onClick={handleShowQRCode}>
                <ListItemIcon>
                  <QrCode2Icon fontSize="small" style={{ color: "#1b5e20" }} />
                </ListItemIcon>
                <ListItemText primary="Generate QR Code" />
              </MenuItem>
            </StyledMenu>
          </RestaurantInfo>
          <div style={{ padding: "0 16px" }}>
            {/* Which of the restaurant's locations this storefront orders
                from, and the dialog to choose another. Nothing for a
                one-location restaurant. */}
            <LocationPicker
              locations={restaurantLocations}
              currentId={outletId || restaurantRow?._id}
              vendorName={selectedActiveRestaurant?.name || restaurantRow?.name}
              onChoose={chooseLocation}
              confirmChoice={cartBoundElsewhere}
              deliveryPoint={deliveryPoint}
              accent="#f24405"
              maxWidth="1440px"
            />
            <RestaurantAvailabilityBanner availability={availability} />
            <CoverageBanner
              variant="block"
              deliverable={coverage.deliverable}
              reason={coverage.reason}
              distanceKm={coverage.distanceKm}
              radius={coverage.radius}
              hasLocation={coverage.hasLocation}
              status={coverage.status}
              error={coverage.error}
              onRetry={coverage.refresh}
              onChangeLocation={() => setShowLocationPrompt(true)}
            />
            {/* Order together: hidden unless the server switches it on for
                restaurants in this market. The ids are the ones the cart
                quotes with (the owner id, and this branch). */}
            <OrderTogetherEntry
              vendorId={selectedActiveRestaurant?.owner || id}
              outletId={outletId}
              restaurantName={restaurantName}
              orderingOpen={availability.canOrder}
            />
          </div>
          {hasMenu ? (
            <>
              <MenuSearchRow>
                <CatalogueSearch
                  value={menuQuery}
                  onChange={setMenuQuery}
                  placeholder="Search this menu (e.g. chicken, pizza, chips)"
                  ariaLabel="Search this menu"
                  accent="#f24405"
                  maxWidth="560px"
                  marginBottom="0"
                  hint={
                    isSearching
                      ? visibleDishCount === 0
                        ? `Nothing on the menu matches "${menuQuery.trim()}"`
                        : `${visibleDishCount} ${visibleDishCount === 1 ? "dish" : "dishes"} in ${visibleSections.length} ${visibleSections.length === 1 ? "category" : "categories"}`
                      : null
                  }
                />
              </MenuSearchRow>
              {isSearching && visibleSections.length === 0 ? (
                <NoMatchMessage>
                  No dishes match &ldquo;{menuQuery.trim()}&rdquo;. Try another
                  word, or{" "}
                  <button type="button" onClick={() => setMenuQuery("")}>
                    show the whole menu
                  </button>
                  .
                </NoMatchMessage>
              ) : null}
              <SectionsNav ref={sectionsNavRef}>
                {visibleSections
                  .map((section) => (
                    <SectionLink
                      key={section._id}
                      href={`#section-${section._id}`}
                      onClick={handleSectionClick(`section-${section._id}`)}
                      className={getLinkClasses(`section-${section._id}`)}
                    >
                      {section.title}
                    </SectionLink>
                  ))}
              </SectionsNav>
              <SectionsContainer data-sections-container>
                {visibleSections
                  .map((section) => (
                    <Section key={section._id} id={`section-${section._id}`}>
                      <SectionHeader>
                        <h2>{section.title}</h2>
                        {section.description && (
                          <p className="section-description">
                            {section.description}
                          </p>
                        )}
                      </SectionHeader>
                      <ProductsGrid>
                        {section.products
                          .map((product) => {
                            const isInCart = Boolean(cart[product._id]);
                            const isImageLoaded = loadedImages[product._id];
                            // A dish can be listed before it has been
                            // photographed. Nothing about that is an error, but
                            // it has to be decided UP FRONT: with no src the
                            // browser fires no load and no error event, so the
                            // skeleton below would shimmer forever and read as
                            // a page that never finished loading.
                            const hasPhoto = Boolean(product.image);
                            /* Sold out, switched off or not sold at THIS
                               branch (4 Oct 2026). The server also sets the
                               legacy soldOutToday for it, for older apps;
                               here the branch words win. */
                            const branchBlocked = branchWords && blockedHere(product);
                            const soldOutToday =
                              branchBlocked || product.soldOutToday === true;
                            const soldOutText = branchBlocked
                              ? soldOutLabel(product, hereName)
                              : "Sold out today";
                            const onOffer = hasSalePrice(product);
                            return (
                              <ProductCard
                                key={product._id}
                                $unavailable={
                                  availability.isClosed ||
                                  coverage.isOutOfRange ||
                                  soldOutToday
                                }
                                onClick={() => handleProductClick(product)}
                              >
                                <ProductImageWrapper>
                                  {hasPhoto && !isImageLoaded && (
                                    <ProductImageSkeleton />
                                  )}
                                  {onOffer && (
                                    <DealTag
                                      discount={product.discount}
                                      size="m"
                                      radius={12}
                                    />
                                  )}
                                  {hasPhoto && (
                                    <ProductImg
                                      src={getOptimizedUrl(
                                        product.image,
                                        "thumbnail",
                                      )}
                                      alt={product.name}
                                      isLoaded={isImageLoaded}
                                      onLoad={() =>
                                        handleImageLoaded(product._id)
                                      }
                                      onError={(e) => {
                                        e.target.style.display = "none";
                                        setLoadedImages((prev) => ({
                                          ...prev,
                                          [product._id]: "fallback",
                                        }));
                                      }}
                                    />
                                  )}
                                  {(!hasPhoto || isImageLoaded === "fallback") && (
                                    <FallbackImageContainer>
                                      <FallbackImage
                                        src={menuPlaceholder}
                                        alt=""
                                        aria-hidden="true"
                                        loading="lazy"
                                      />
                                    </FallbackImageContainer>
                                  )}
                                  <ProductDetailBadge $belowDeal={onOffer}>
                                    <InfoOutlinedIcon
                                      style={{ fontSize: 16 }}
                                    />
                                    Details
                                  </ProductDetailBadge>
                                  {soldOutToday ? (
                                    <SoldOutBadge>{soldOutText}</SoldOutBadge>
                                  ) : availability.isClosed ? (
                                    <ClosedBadge>Closed</ClosedBadge>
                                  ) : availability.isUnknown ? (
                                    <OutOfRangeBadge>
                                      Hours unavailable
                                    </OutOfRangeBadge>
                                  ) : availability.isChecking ? (
                                    <CheckingBadge>
                                      Checking hours
                                    </CheckingBadge>
                                  ) : coverage.isOutOfRange ? (
                                    <OutOfRangeBadge>
                                      Outside delivery area
                                    </OutOfRangeBadge>
                                  ) : null}
                                </ProductImageWrapper>
                                <ProductDetails>
                                  <ProductName>{product.name}</ProductName>
                                  {branchBlocked && (
                                    <BranchAvailabilityBadge
                                      compact
                                      showLabel={false}
                                      product={product}
                                      branchName={hereName}
                                      locations={restaurantLocations}
                                      vendorName={restaurantName}
                                      onGetItFrom={getItFrom.request}
                                      accent="#f24405"
                                    />
                                  )}
                                  <QuillWrapper>
                                    <ReactQuill
                                      value={truncateDescription(
                                        product.description ||
                                          "No description available.",
                                      )}
                                      readOnly={true}
                                      theme="bubble"
                                      className="quill"
                                    />
                                  </QuillWrapper>
                                  <PriceAndButtonContainer>
                                    <PriceCut
                                      component={ProductPrice}
                                      price={product.price}
                                      listPrice={product.listPrice}
                                      salePrice={product.salePrice}
                                      format={formatCurrency}
                                    />
                                    <ProductCardActions>
                                      <Tooltip title="Click to copy · Right-click for WhatsApp">
                                        <ProductShareButton
                                          onClick={(e) =>
                                            handleProductShare(e, product)
                                          }
                                          onContextMenu={(e) => {
                                            e.preventDefault();
                                            handleProductWhatsAppShare(
                                              e,
                                              product,
                                            );
                                          }}
                                          aria-label="Share product"
                                        >
                                          <ShareIcon style={{ fontSize: 16 }} />
                                        </ProductShareButton>
                                      </Tooltip>
                                      <Tooltip
                                        title={
                                          branchBlocked
                                            ? addButtonLabel(product)
                                            : soldOutToday
                                            ? `${product.name} has sold out for today. Back on the menu tomorrow.`
                                            : availability.isClosed
                                            ? availability.message
                                            : availability.isUnknown
                                              ? "We can't confirm this restaurant's hours — retry above"
                                              : availability.isChecking
                                                ? "Confirming restaurant hours…"
                                                : coverage.isOutOfRange
                                                  ? "Outside delivery area — change your location to order"
                                                  : isInCart
                                                    ? "Item in cart"
                                                    : "Add to Cart"
                                        }
                                      >
                                        {/* span: MUI needs a live wrapper to
                                            anchor a tooltip on a disabled
                                            control. */}
                                        <span>
                                          <AddToCartButton
                                            isInCart={isInCart}
                                            disabled={
                                              !availability.canOrder ||
                                              coverage.isOutOfRange ||
                                              soldOutToday
                                            }
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleProductClick(product);
                                            }}
                                          >
                                            {isInCart ? (
                                              <InCartIcon />
                                            ) : (
                                              <AddToCartIcon />
                                            )}
                                          </AddToCartButton>
                                        </span>
                                      </Tooltip>
                                    </ProductCardActions>
                                  </PriceAndButtonContainer>
                                </ProductDetails>
                              </ProductCard>
                            );
                          })}
                      </ProductsGrid>
                    </Section>
                  ))}
              </SectionsContainer>
            </>
          ) : (
            <NoProductsMessage>
              No Active Products Available at the moment...
            </NoProductsMessage>
          )}
        </>
      )}
      {/* No MUI <Tooltip> here on purpose. Its popper opens on long-press on
          touch and flashes in and out around the tap, which is the other half
          of the flicker. `aria-label` covers assistive tech and `title` gives
          desktop the same hint without a popper. */}
      {!isCartVisible && (
      <FloatingCartButton
          title={cartCount > 0 ? `View cart (${cartCount})` : "View cart"}
          type="button"
          onClick={() => setIsCartVisible(!isCartVisible)}
          aria-label={
            cartCount > 0
              ? `View cart, ${cartCount} item${cartCount === 1 ? "" : "s"}`
              : "View cart"
          }
          aria-expanded={isCartVisible}
        >
          <CartBadge
            badgeContent={cartCount}
            invisible={cartCount === 0}
            overlap="circular"
          >
            <ShoppingCartIcon />
          </CartBadge>
        </FloatingCartButton>
      )}
      {isCartVisible && (
        <CartOverlay>
          <RestaurantCart onClose={() => setIsCartVisible(false)} />
        </CartOverlay>
      )}
      {/* "Changes at <branch>", after the cart moved to another branch. */}
      <BranchCartReviewDialog {...branchDialogProps} />
      {showLocationPrompt && (
        <LocationPrompt open onClose={() => setShowLocationPrompt(false)} />
      )}
      {selectedProduct && (
        <RestaurantProductDialog
          open={Boolean(selectedProduct)}
          onClose={handleCloseDialog}
          product={selectedProduct}
          optionGroups={selectedProduct.optionGroups}
          restaurant={selectedActiveRestaurant || restaurantDetails?.restaurant}
          outletId={outletId}
          branchName={hereName}
          cartBranchName={nameOfLocation(cartLocation)}
          onCartMoved={reviewNextCheck}
          orderingDisabled={
            !availability.canOrder ||
            selectedProduct.soldOutToday === true ||
            blockedHere(selectedProduct)
          }
          orderingDisabledTitle={
            branchWords && blockedHere(selectedProduct)
              ? soldOutLabel(selectedProduct, hereName)
              : selectedProduct.soldOutToday === true
              ? "Sold out for today"
              : availability.isClosed
                ? "Restaurant closed"
                : availability.isUnknown
                  ? "Hours unavailable"
                  : "Confirming hours"
          }
          orderingDisabledMessage={
            branchWords && blockedHere(selectedProduct)
              ? blockedMessage(selectedProduct, selectedProduct.name)
              : selectedProduct.soldOutToday === true
              ? `${selectedProduct.name} has sold out for today. It will be back on the menu tomorrow.`
              : availability.message
          }
          onCheckAvailability={() => availability.refresh()}
          // The way out, as in the apps: "Get it from" the best branch that
          // has the dish, or that no branch has it right now.
          branchAction={
            branchWords && blockedHere(selectedProduct) ? (
              <BranchAvailabilityBadge
                showLabel={false}
                product={selectedProduct}
                branchName={hereName}
                locations={restaurantLocations}
                vendorName={restaurantName}
                onGetItFrom={(picked) => {
                  handleCloseDialog();
                  getItFrom.request(picked);
                }}
                accent="#f24405"
              />
            ) : null
          }
        />
      )}
      {/* "Get it from <branch>" with dishes from another branch in the cart. */}
      <OutletSwitchDialog
        open={Boolean(getItFrom.pending)}
        fromLabel={nameOfLocation(cartLocation)}
        toLabel={locationName(getItFrom.pending, restaurantName)}
        onCancel={getItFrom.cancel}
        onConfirm={getItFrom.confirm}
      />

      {/* Visual share poster modal (branded poster preview + share actions). */}
      <ProductShareModal
        visible={Boolean(shareProduct)}
        item={shareProduct}
        shopUrl={shareProductUrl}
        onClose={() => setShareProduct(null)}
      />
      <Dialog
        open={qrCodeOpen}
        onClose={handleCloseQRCode}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: "16px",
            padding: "8px",
          },
        }}
      >
        <DialogTitle
          style={{
            textAlign: "center",
            paddingBottom: "8px",
            fontWeight: "600",
          }}
        >
          {selectedActiveRestaurant?.name || "Restaurant"} QR Menu
        </DialogTitle>
        <DialogContent
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "24px",
          }}
        >
          <Box
            ref={qrCodeRef}
            style={{
              padding: "24px",
              backgroundColor: "#ffffff",
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <QRCodeCanvas
              value={getRestaurantUrl()}
              size={256}
              level="H"
              includeMargin={true}
            />
          </Box>
          <Typography
            variant="body2"
            color="textSecondary"
            style={{
              textAlign: "center",
              marginTop: "8px",
              wordBreak: "break-word",
            }}
          >
            Scan this QR code to view the restaurant menu on Lusuku!
          </Typography>
          <Typography
            variant="caption"
            color="textSecondary"
            style={{
              textAlign: "center",
              marginTop: "8px",
              fontSize: "0.75rem",
              color: "#666",
            }}
          >
            {getRestaurantUrl()}
          </Typography>
        </DialogContent>
        <DialogActions
          style={{
            padding: "16px 24px",
            justifyContent: "space-between",
            gap: "8px",
          }}
        >
          <Button
            onClick={handleDownloadQRCode}
            variant="outlined"
            startIcon={<DownloadIcon />}
            style={{
              borderRadius: "8px",
              textTransform: "none",
              padding: "8px 16px",
              flex: 1,
            }}
          >
            Download
          </Button>
          <Button
            onClick={handleShareQRCode}
            variant="outlined"
            startIcon={<ShareIcon />}
            color="success"
            style={{
              borderRadius: "8px",
              textTransform: "none",
              padding: "8px 16px",
              flex: 1,
            }}
          >
            Share
          </Button>
          <Button
            onClick={handleCloseQRCode}
            variant="contained"
            color="primary"
            style={{
              borderRadius: "8px",
              textTransform: "none",
              padding: "8px 16px",
              flex: 1,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restaurant-level reviews — anchored to the restaurant id from the
          URL so they fan out via the per-subject socket room. */}
      {id && (
        <Reviews
          subjectType="vendor_restaurant"
          subjectId={id}
          title={`Reviews for ${restaurantDetails?.name || "this restaurant"}`}
        />
      )}
      {/* Both floats are hidden while the cart sheet is open. They are fixed
          at z-index 1200 against the overlay's 1000, so they float ON TOP
          of the sheet and sit squarely over the Total and the checkout button
          — the two things somebody opened the sheet to see. Hiding the cart
          button also removes the odd state where the control that opens a
          panel is still offering to open it while it is open. */}
      {!isCartVisible && (
      <VendorWhatsAppFloat
        phone={
          selectedActiveRestaurant?.phone || restaurantDetails?.vendor?.phone
        }
        vendorName={
          selectedActiveRestaurant?.name ||
          restaurantDetails?.vendor?.name ||
          restaurantDetails?.name
        }
        vendorType="restaurant"
        pageUrl={`${SITE_ORIGIN}/restaurant/shop/${buildVendorParam(selectedActiveRestaurant?.name, id)}`}
      />
      )}
    </Container>
  );
};

export default RestaurantShopPage;

const Container = styled.div`
  padding: 20px;
  padding-top: var(
    --lusuku-nav-h,
    125px
  ); /* Sits flush under the fixed navbar */

  /* The nav variable alone puts the banner FLUSH against the search field,
     which reads as the banner being part of the header rather than the
     first thing on the page. On a phone the navbar is three stacked rows
     deep, so that seam is right under a busy element and there is nothing
     to separate them. The extra 14px is the gap, not a bigger inset. */
  @media (max-width: 850px) {
    padding: 10px;
    padding-top: calc(var(--lusuku-nav-h, 125px) + 30px);
  }
`;

const CartOverlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;

  /* align-items:center on a panel TALLER than the overlay pushes its top
     and bottom out of view at the same time, and a flex container does not
     scroll to reach them. Any panel that outgrows the screen therefore had
     its footer permanently out of reach. Scrolling the overlay itself is the
     safety net; the panel sizing above is the actual fix. */
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;

  /* On a phone the panel is a bottom sheet, so it aligns to the bottom edge
     rather than floating in the middle of a dimmed screen. */
  @media (max-width: 520px) {
    align-items: flex-end;
  }
`;

const RestaurantInfo = styled.div`
  position: relative;
  width: 100%;
  max-width: 1440px;
  aspect-ratio: 1440 / 175;
  margin-left: auto;
  margin-right: auto;
  overflow: hidden;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  margin-bottom: 8px;

  /* Phones do NOT get the desktop treatment, and the reason is arithmetic
     rather than taste. The banner is a 1440x175 strip (8.2:1). This block
     used to force it into "height: 200px; aspect-ratio: auto", so on a
     284px-wide card "object-fit: cover" had to scale the strip UP to
     1646x200 and then crop it to 284px. The phone was being shown 17% of
     the artwork, upscaled — which is why the banner read as a dark smear
     while the desktop version looked fine.

     So on mobile the card stops pretending the strip is a photo frame. It
     becomes a two-part stack: a shallow image band on top (art-directed by
     Cloudinary — see MOBILE_BANNER_RATIO) with the identity row flowing
     UNDERNEATH it on a solid surface instead of floating over the image.
     Height is whatever that content needs, which lands near 165px — less
     than the 200px it replaces, while showing far more of the banner and
     giving the restaurant's name a full-width line to sit on. */
  @media (max-width: 768px) {
    aspect-ratio: auto;
    height: auto;
    display: flex;
    flex-direction: column;
    /* Deepened from the navbar's own #324d54 so the card reads as part of
       the header family rather than an unexplained black bar. */
    background: linear-gradient(160deg, #33505a 0%, #22383f 100%);
    margin-bottom: 10px;
    border-radius: 12px;
  }
`;

/* Wrapper for the art-directed <picture>. It owns the geometry so the
   <img> can stay a dumb `fill the box` element in both modes. */
const BannerPicture = styled.picture`
  display: block;
  width: 100%;
  height: 100%;

  @media (max-width: 768px) {
    height: auto;
    aspect-ratio: 5 / 1; /* keep in step with MOBILE_BANNER_RATIO */
  }
`;

const RestaurantBannerImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const RestaurantInfoOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.85),
    rgba(0, 0, 0, 0.4) 60%,
    transparent
  );
  padding: 20px;
  display: flex;
  align-items: flex-end;
  gap: 15px;

  /* Mobile: no longer an overlay. The band above it is only ~75px tall, far
     too short to sit three lines of text on, so this becomes a normal row
     in the stack. "relative" (not "static") because the share button is
     pinned to this row's bottom-right corner below. The scrim goes with
     the absolute positioning — a gradient over a solid colour instead of
     over a photograph is just a grey smudge. */
  @media (max-width: 768px) {
    position: relative;
    bottom: auto;
    background: none;
    padding: 12px;
    gap: 12px;
    align-items: center;
  }
`;

/* The logo badge. Every `!important` below is load-bearing, so read before
   trimming: MUI v5 injects its emotion stylesheet AFTER styled-components',
   so `.MuiAvatar-root`'s own `width: 40px; height: 40px` was beating the
   plain `width: 90%` this block used to declare. The result: the logo
   painted at 30x30 inside a 90px tile. Same story for `border-radius`
   (MuiAvatar-circular = 50%) and `background-color` (grey[400], which would
   otherwise show as grey bars either side of a contained logo).
   VendorShopPage already carries this guard; this page never got it.

   The nested `.MuiAvatar-img` rule is separate and also required: MUI
   hard-codes `object-fit: cover` on the inner <img>, so declaring
   object-fit on the root does nothing and wide wordmark logos get their
   sides cropped off. */
const StyledAvatar = styled(Avatar)`
  width: 100% !important;
  height: 100% !important;
  border-radius: 8px !important;
  background-color: transparent !important;
  padding: 6px;
  box-sizing: border-box;

  .MuiAvatar-img {
    object-fit: contain;
  }

  @media (max-width: 768px) {
    padding: 4px;
  }
`;

/* Shown when a restaurant has no logo uploaded. Mirrors the placeholder
   treatment on VendorShopPage / AllVendorsPage so the badge reads as
   deliberate branding rather than a broken image. */
const AvatarPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(
      circle at 28% 24%,
      rgba(255, 255, 255, 0.55) 0%,
      rgba(255, 255, 255, 0.1) 32%,
      transparent 58%
    ),
    linear-gradient(145deg, #ffb54b 0%, #f7941d 48%, #de6e05 100%);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.5),
    inset 0 -2px 6px rgba(0, 0, 0, 0.12);

  svg {
    color: #fff;
    font-size: 42px;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.25));
  }

  @media (max-width: 768px) {
    svg {
      font-size: 32px;
    }
  }
`;

const AvatarWrapper = styled.div`
  width: 90px;
  height: 90px;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 12px;
  overflow: hidden;
  border: 3px solid white;

  @media (max-width: 768px) {
    width: 65px;
    height: 65px;
    border-radius: 10px;
    border-width: 2px;
  }
`;

/* The vendor's own tagline under the name, in the header's white type.
   Nothing renders when the vendor has not written one
   (components/VendorTagline.jsx). && outranks the header's own p rule. */
const HeaderTagline = styled(VendorTagline)`
  && {
    margin: 4px 0 6px;
    max-width: 560px;
    font-size: 0.95rem;
    line-height: 1.4;
    color: rgba(255, 255, 255, 0.92);
  }
`;

const RestaurantText = styled.div`
  flex: 1;
  color: white;
  min-width: 0;

  h1 {
    font-size: 1.6rem;
    margin: 0 0 4px 0;
    font-weight: 700;
    text-shadow: 1px 1px 4px rgba(0, 0, 0, 0.5);
    letter-spacing: 0.3px;
    font-family: "Poppins", sans-serif;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    /* The name is the one thing on this card that must never be
       abbreviated. In the old layout it had 129px of a 284px card to work
       with — the avatar, the share button and two gaps ate the rest — so
       "Cakely By Asha Batenga" needed 248px and rendered as
       "Cakely By Bate…". Now that the row sits below the image it has the
       full width, and wrapping is the correct answer for the few names
       still too long for one line. */
    @media (max-width: 768px) {
      font-size: 1.15rem;
      white-space: normal;
      overflow: visible;
      text-overflow: clip;
      overflow-wrap: anywhere;
      line-height: 1.25;
    }
  }

  p {
    margin: 2px 0;
    font-size: 0.9rem;
    text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.4);
    font-weight: 400;
    opacity: 0.9;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    /* Address gets two lines rather than one ellipsised one. The
       padding-right keeps that second line clear of the share button,
       which is pinned to the row's bottom-right corner on mobile. */
    @media (max-width: 768px) {
      font-size: 0.8rem;
      white-space: normal;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      padding-right: 44px;
    }
  }
`;

const ShareButton = styled(IconButton)`
  color: white !important;
  background-color: rgba(255, 255, 255, 0.2) !important;
  flex-shrink: 0;
  padding: 10px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background-color: rgba(255, 255, 255, 0.35) !important;
    transform: scale(1.1);
  }

  svg {
    font-size: 22px;
  }

  /* Taken out of the flex row on mobile and pinned to the corner instead.
     In flow it cost the name 48px of a 284px card; out of flow it costs
     nothing, and the only text that reaches down beside it is the star
     line, which is ~70px wide. */
  @media (max-width: 768px) {
    padding: 8px;
    position: absolute;
    right: 8px;
    bottom: 8px;
    svg {
      font-size: 20px;
    }
  }
`;

const SectionsNav = styled.nav`
  /* Pinned under the Navbar at the height it actually rendered. A fixed 121px
     was the desktop height; on a phone the bar is three rows deep and the
     strip sat over the search field as soon as the page had enough below the
     menu (the reviews block) to scroll that far. */
  ${stickyUnderNav}
  background-color: #ffffff;
  z-index: 1000;
  padding: 4px 0;
  display: flex;
  justify-content: space-around;
  border-bottom: 2px solid #f0f0f0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  backdrop-filter: blur(10px);

  @media (max-width: 850px) {
    overflow-x: auto;
    white-space: nowrap;
    margin-bottom: 8px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const SectionLink = styled.a`
  text-decoration: none;
  color: #333;
  padding: 7px 24px;
  font-weight: 500;
  font-size: 15px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 8px;
  position: relative;

  &.active-section {
    color: #f24405;
    font-weight: 700;
    background-color: #ffe8e0;
    border-bottom: 3px solid #84b026;
    box-shadow: 0 2px 8px rgba(242, 68, 5, 0.15);
    transition: all 0.2s ease;
  }

  &:hover {
    background-color: #f5f5f5;
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 14px;
    display: inline-block;
  }
`;

const SectionsContainer = styled.div`
  position: relative;
  margin-top: 2px;
  overflow-y: auto;
  /* Exactly what the two bars leave of the viewport, so the list's bottom
     lands at the screen's bottom with the strip pinned. 160 and 270 were
     guesses at the same sum. */
  ${heightUnderStack(2)}
  padding-bottom: 80px;
  scroll-behavior: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 768px) {
    padding-bottom: 100px;
  }
`;

const Section = styled.section`
  margin-bottom: 20px;
  padding-top: 6px;
  ${sectionScrollMargin}

  @media (max-width: 768px) {
    margin-bottom: 8px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 6px;
  padding: 8px 16px;
  background-color: #ffffff;
  border-left: 5px solid #f24405;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.8);
  position: relative;
  gap: 16px;

  &:before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 5px;
    background: #84b026;
    border-radius: 8px 0 0 8px;
    transform: scaleY(0);
    transform-origin: bottom;
    transition: transform 0.3s ease;
  }

  &:hover:before {
    transform: scaleY(1);
  }

  h2 {
    margin: 0;
    font-size: 1.25em;
    font-weight: 700;
    color: #1a1a1a;
    position: relative;
    white-space: nowrap;
    padding-right: 20px;

    &:after {
      content: "";
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 8px;
      height: 8px;
      background-color: #f24405;
      border-radius: 50%;
      box-shadow: 0 0 0 3px rgba(242, 68, 5, 0.2);
    }

    @media (max-width: 768px) {
      font-size: 1em;
      padding-right: 16px;
    }
  }

  @media (max-width: 768px) {
    padding: 10px 14px;
    margin-bottom: 10px;
  }

  .section-description {
    margin: 0;
    font-size: 0.9em;
    font-style: italic;
    color: #666;
    font-weight: 400;
    line-height: 1.4;
    flex: 1;
    padding-left: 16px;
    border-left: 2px solid #f0f0f0;
    position: relative;

    &:before {
      content: "";
      position: absolute;
      left: -6px;
      top: 50%;
      transform: translateY(-50%);
      width: 10px;
      height: 10px;
      background-color: #84b026;
      border-radius: 50%;
      border: 2px solid white;
    }

    @media (max-width: 768px) {
      display: none;
    }
  }
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 18px;
  padding: 8px 4px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
`;

const ProductCard = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  position: relative;
  cursor: pointer;
  height: 100%;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
    border-color: #84b026;
  }

  /* Unorderable menu: the item stays readable and still opens, it just stops
     advertising itself. Desaturation carries the state at a glance without
     hiding the food. Applied to the media/price only — the name stays at full
     contrast so the menu is still legible. */
  ${(props) =>
    props.$unavailable &&
    `
    box-shadow: none;
    border-color: #e6e6e6;

    ${ProductImageWrapper} {
      filter: grayscale(0.85);
      opacity: 0.72;
    }

    &:hover {
      transform: none;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      border-color: #e6e6e6;
    }
  `}
`;

const ProductName = styled.h4`
  font-size: 15px;
  font-weight: 600;
  margin: 0;
  padding: 0;
  color: #1a1a1a;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const QuillWrapper = styled.div`
  flex: 1;
  overflow: hidden;

  .quill {
    font-size: 12px;
    color: #666;
    line-height: 1.4;
    padding: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .ql-editor {
    padding: 0;
    overflow: hidden;
  }
`;

const ProductDetails = styled.div`
  padding: 12px;
  position: relative;
  padding-bottom: 18px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;

  @media (max-width: 768px) {
    padding: 10px;
    padding-bottom: 14px;
    gap: 4px;
  }
`;

const ProductPrice = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: #2d8a2d;
  padding-left: 8px;
  ${cardPriceRow}
  ${cardPriceOwnsItsRow}

  @media (max-width: 768px) {
    font-size: 13px;
    padding-left: 0;
  }
`;

const PriceAndButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  padding-top: 4px;
  margin-left: -8px;
  ${cardFooterRow}

  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const ProductCardActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  ${cardActionsRow}
`;

const ProductShareButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1.5px solid #e0e0e0;
  background: #f8f8f8;
  color: #555;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  padding: 0;

  &:hover {
    background: #eaf4d3;
    border-color: #84b026;
    color: #5a8a00;
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    width: 28px;
    height: 28px;
  }
`;

const AddToCartButton = styled(
  React.forwardRef(({ isInCart, ...otherProps }, ref) => (
    <IconButton ref={ref} {...otherProps} />
  )),
)`
  background-color: ${(props) => (props.isInCart ? "#e0ffe0" : "#fff")};
  border: 1px solid ${(props) => (props.isInCart ? "#00ff00" : "#e0e0e0")};
  border-radius: 50%;
  padding: 5px;
  box-shadow: 0 0 5px 3px ${(props) => (props.isInCart ? "lime" : "#F28705")};
  &:hover {
    background-color: ${(props) => (props.isInCart ? "#d0ffd0" : "#f0f0f0")};
  }

  @media (max-width: 768px) {
    padding: 3px;
  }
`;

const OutOfRangeBadge = styled.div`
  position: absolute;
  top: 8px;
  /* Right-aligned: the hover-revealed "Details" pill owns the top-left corner
     and the two chips used to sit on top of each other. */
  right: 8px;
  background: rgba(138, 43, 6, 0.92);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding: 4px 8px;
  border-radius: 6px;
  z-index: 2;
  pointer-events: none;
`;

/* Sold out for the day: the platform red the shop tiles already use for
   "SOLD OUT", so a dish that ran out reads as the same family as a product
   that ran out. Back tomorrow by itself, which the dialog says. */
const SoldOutBadge = styled(OutOfRangeBadge)`
  background: rgba(239, 68, 68, 0.92);
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

/* "Closed" is a settled, factual verdict — it reads as a hard state, not as a
   warning the shopper can act their way out of. */
const ClosedBadge = styled(OutOfRangeBadge)`
  background: rgba(28, 32, 36, 0.94);
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

/* "Checking hours" is transient and must never be mistaken for "Closed" —
   quiet, low-contrast, no alarm colour. */
const CheckingBadge = styled(OutOfRangeBadge)`
  background: rgba(69, 90, 100, 0.72);
  font-weight: 500;
`;

const AddToCartIcon = styled(ShoppingCartIcon)`
  font-size: 2.8rem !important;
  color: #f24405;
  cursor: pointer;
  transition: color 0.2s;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  border-radius: 50%;
  padding: 10px;

  &:hover {
    color: #f24405;
  }

  @media (max-width: 768px) {
    font-size: 1.6rem !important;
    padding: 6px;
  }
`;

const InCartIcon = styled(CheckCircleIcon)`
  font-size: 2.6rem !important;
  color: #84b026;
  cursor: pointer;
  transition: color 0.2s;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  border-radius: 50%;
  padding: 10px;

  &:hover {
    color: #f24405;
  }

  @media (max-width: 768px) {
    font-size: 1.6rem !important;
    padding: 6px;
  }
`;

/* Was a `styled.div` holding a cart EMOJI with an onClick. Three problems in
   one element, and all three are what "tapping feels off" actually meant:

   1. A div is not a button. No keyboard focus, no Enter/Space, nothing for a
      screen reader, and no `:focus-visible` ring. The tap worked only because
      a click handler happens to fire on anything.
   2. The icon was a text EMOJI, so its glyph, weight and baseline came from
      whatever font the device happened to use. It sat off-centre inside a
      circle sized by `padding` around that glyph, which is why the target felt
      slightly wrong rather than plainly broken.
   3. `background-color: #00ff00` is not a Lusuku green, it is pure sRGB lime.
      Against it MUI's `color="secondary"` badge had almost nothing to hold on
      to, which is the contrast complaint.

   Now a real <button> at a fixed 56px (48 is the minimum comfortable target,
   56 gives the badge room), a centred vector icon, and the brand's own two
   colours — see the background rule below for why it is the ink that fills. */
const FloatingCartButton = styled.button`
  position: fixed;
  bottom: 88px;
  /* Same right offset as VendorWhatsAppFloat (22px / 16px) so the two stack
     on ONE vertical axis instead of sitting 2px out of line. */
  right: 22px;
  width: 56px;
  height: 56px;
  padding: 0;
  border: none;
  border-radius: 50%;
  /* The app's own two colours: the dark ground the hero and the nav are
     painted in, with the gold on top. Before this, every control in this
     corner had invented its own accent, and the cart alone had worn green and
     then red.

     Two reasons the ink fills and the gold draws, rather than the reverse. The
     brand already uses the pair that way, gold ON dark and never as a field of
     its own. And gold is a LIGHT colour: a solid gold circle on this white
     page measures 1.71:1 and has almost no edge, so it reads as floating,
     where the ink measures 16.01:1 and is the most present thing on screen.
     The gold still carries the meaning, in the glyph at 9.36:1 and the count.

     It also settles the button directly below. VendorWhatsAppFloat is #128c4a.
     Green-on-green read as one component rendered twice, so this went red — but
     red against that green measures 1.15:1, so the separation was carried
     entirely by hue and none of it by lightness. That is invisible in
     greyscale, and it is the exact pairing red-green colour blindness
     collapses. Ink against the same green is 3.72:1, which survives both. */
  background: linear-gradient(
    135deg,
    ${BRAND_INK_LIFT} 0%,
    ${BRAND_INK} 55%,
    ${BRAND_INK_DEEP} 100%
  );
  color: ${BRAND_GOLD};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  /* Stops the grey flash Android paints over the whole circle on tap; the
     :active transform below is the feedback instead. */
  -webkit-tap-highlight-color: transparent;
  box-shadow:
    0 4px 6px rgba(0, 0, 0, 0.16),
    0 10px 20px rgba(20, 36, 39, 0.34),
    0 20px 40px rgba(20, 36, 39, 0.18),
    0 1px 3px rgba(0, 0, 0, 0.1);
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    background 0.2s ease;
  /* Above storefront content, below MUI dialogs (1300). */
  z-index: 1200;

  svg {
    font-size: 26px;
  }

  /* THE FLICKER. A touch browser applies :hover on tap and keeps it until you
     tap somewhere else, so this lifted, scaled state fired on every press and
     then snapped back — which is exactly what "flickers like I am inspecting
     elements" looks like. Gating on an actual hovering pointer leaves phones
     with only the :active press below, which is the feedback they want. */
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      /* Hover deepens the ground so the gold comes forward, rather than
         changing the accent — the lift reads as the button waking up. */
      background: linear-gradient(
        135deg,
        ${BRAND_INK} 0%,
        ${BRAND_INK_DEEP} 55%,
        #060e0f 100%
      );
      transform: translateY(-3px) scale(1.06);
      box-shadow:
        0 6px 10px rgba(0, 0, 0, 0.18),
        0 16px 30px rgba(20, 36, 39, 0.42),
        0 28px 50px rgba(20, 36, 39, 0.22),
        0 1px 3px rgba(0, 0, 0, 0.12);
    }
  }

  &:focus-visible {
    /* Gold, not white. The circle is dark and the page behind it is white, so
       a white ring is the one colour that disappears on both sides. */
    outline: 3px solid ${BRAND_GOLD};
    outline-offset: 2px;
    box-shadow: 0 0 0 6px rgba(20, 36, 39, 0.5);
  }

  &:active {
    transform: scale(0.94);
  }

  @media (max-width: 768px) {
    bottom: 74px;
    right: 16px;
  }

  @media (max-width: 480px) {
    bottom: 74px;
    right: 16px;
    /* Deliberately NOT smaller than the desktop size. This is the one control
       on the page a thumb reaches for most, and the old rule shrank its
       padding on exactly the screens where the target matters most. */
  }
`;

/* The count. Every version of this has been settled the same way and the rule
   is worth keeping visible: the separation must be carried by LIGHTNESS, not
   by hue. A hue-only pairing disappears in greyscale, and disappears again for
   the roughly 8% of men who cannot separate red from green.

   Gold pill, ink numeral. On the dark circle the pill is the light element and
   is seen first; the numeral on it measures 9.36:1. The ink hairline stops the
   pill dissolving into the glyph on the side where the two golds meet. */
const CartBadge = styled(Badge)`
  .MuiBadge-badge {
    background-color: ${BRAND_GOLD};
    color: ${BRAND_INK};
    font-weight: 800;
    font-size: 11px;
    min-width: 20px;
    height: 20px;
    padding: 0 5px;
    border: 1.5px solid ${BRAND_INK};
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.32);
  }
`;

/* ======================== */
/* Shimmer Skeleton Styles  */
/* ======================== */

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const SkeletonPulse = styled.div`
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 37%, #f0f0f0 63%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.6s ease-in-out infinite;
  border-radius: 8px;
`;

const SkeletonContainer = styled.div`
  padding-top: 10px;
  animation: ${fadeIn} 0.3s ease;
`;

/* Banner. This has to mirror RestaurantInfo's GEOMETRY, not a number someone
   remembered: RestaurantInfo is `aspect-ratio: 1440/175` capped at 1440 wide,
   which is about 146px at a 1204px viewport, and this block was claiming a
   flat 250px. Every load therefore collapsed the banner by ~100px the moment
   the data arrived. Change one of these two blocks and change the other. */
const SkeletonBanner = styled.div`
  width: 100%;
  max-width: 1440px;
  aspect-ratio: 1440 / 175;
  margin-left: auto;
  margin-right: auto;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  background: linear-gradient(90deg, #e8e8e8 25%, #d8d8d8 37%, #e8e8e8 63%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.6s ease-in-out infinite;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  margin-bottom: 8px;

  /* Mirrors RestaurantInfo's mobile stack: shimmering image band on top,
     identity row beneath on the same surface colour. The band's height is
     reserved by the ::before rather than by an <img>, so the card does not
     resize the instant the real banner lands. */
  @media (max-width: 768px) {
    aspect-ratio: auto;
    height: auto;
    display: flex;
    flex-direction: column;
    background: linear-gradient(160deg, #33505a 0%, #22383f 100%);
    animation: none;
    margin-bottom: 10px;
    border-radius: 12px;

    &::before {
      content: "";
      display: block;
      width: 100%;
      aspect-ratio: 5 / 1; /* == BannerPicture's mobile band */
      background: linear-gradient(90deg, #e8e8e8 25%, #d8d8d8 37%, #e8e8e8 63%);
      background-size: 800px 100%;
      animation: ${shimmer} 1.6s ease-in-out infinite;
    }
  }
`;

/* Bottom overlay: mirrors RestaurantInfoOverlay, same stops so the scrim does
   not visibly lighten when the real banner replaces this. */
const SkeletonBannerOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.85),
    rgba(0, 0, 0, 0.4) 60%,
    transparent
  );
  padding: 20px;
  display: flex;
  align-items: flex-end;
  gap: 15px;

  @media (max-width: 768px) {
    position: relative;
    bottom: auto;
    background: none;
    padding: 12px;
    gap: 12px;
    align-items: center;
  }
`;

/* Avatar: mirrors AvatarWrapper, which is 90/65 with a 3px/2px border, not
   the 80/60 this used to claim. Same box model on both, so the outer size
   lands identically and the badge does not resize on load. */
const SkeletonAvatarBox = styled.div`
  width: 90px;
  height: 90px;
  min-width: 90px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
  border: 3px solid rgba(255, 255, 255, 0.3);

  @media (max-width: 768px) {
    width: 65px;
    height: 65px;
    min-width: 65px;
    border-radius: 10px;
    border-width: 2px;
  }
`;

const SkeletonBannerText = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;

  /* Mobile reserves TWO lines for the address rather than one. Once the row
     moved below the image the address stopped being ellipsised and started
     wrapping (see RestaurantText), so a one-line reservation left the card
     growing ~20px the instant the data arrived — the same class of jump the
     comment on SkeletonBanner is about. The child selector out-specifies
     SkeletonLine's own height prop, so no !important is needed. */
  @media (max-width: 768px) {
    > *:nth-child(2) {
      height: 34px;
    }
  }
`;

/* Share button: mirrors ShareButton, whose real size is its 22px icon plus
   10px of padding either side (20px icon plus 8px on mobile). */
const SkeletonShareBtn = styled.div`
  width: 42px;
  height: 42px;
  min-width: 42px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  margin-left: auto;
  align-self: flex-end;

  /* Pinned to the corner, mirroring ShareButton's mobile rule. */
  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    min-width: 36px;
    position: absolute;
    right: 8px;
    bottom: 8px;
  }
`;

/* Lines inside the overlay use semi-transparent white to appear on the dark gradient */
const SkeletonLine = styled(SkeletonPulse)`
  width: ${(props) => props.width || "100%"};
  height: ${(props) => props.height || "14px"};
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.08) 25%,
    rgba(255, 255, 255, 0.18) 37%,
    rgba(255, 255, 255, 0.08) 63%
  );
  background-size: 800px 100%;
`;

/* Nav — mirrors SectionsNav: space-around, sticky look, rounded top, bottom border */
const SkeletonNav = styled.div`
  display: flex;
  justify-content: space-around;
  padding: 12px 0;
  border-bottom: 2px solid #f0f0f0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  background: #ffffff;
`;

const SkeletonNavItem = styled(SkeletonPulse)`
  width: ${(props) => props.width || "80px"};
  height: 36px;
  border-radius: 8px;
  ${(props) =>
    props.isFirst &&
    `
    background: linear-gradient(90deg, #ffe8e0 25%, #ffddd2 37%, #ffe8e0 63%);
    background-size: 800px 100%;
  `}
`;

/* Section header — mirrors SectionHeader: left border, dot, same padding/shadow */
const SkeletonSectionTitle = styled.div`
  display: flex;
  align-items: center;
  margin-top: 24px;
  margin-bottom: 24px;
  padding: 18px 20px;
  border-left: 5px solid #f24405;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  gap: 8px;
`;

/* Red dot — mirrors the h2::after pseudo-element */
const SkeletonDot = styled.div`
  width: 8px;
  height: 8px;
  min-width: 8px;
  border-radius: 50%;
  background-color: #f24405;
  box-shadow: 0 0 0 3px rgba(242, 68, 5, 0.2);
`;

/* Product grid — mirrors ProductsGrid exact breakpoints */
const SkeletonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 18px;
  padding: 8px 4px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
`;

/* Product card — mirrors ProductCard */
const SkeletonCard = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

/* Image — mirrors ProductImageWrapper height */
const SkeletonImage = styled(SkeletonPulse)`
  width: 100%;
  height: 220px;
  border-radius: 0;

  @media (max-width: 768px) {
    height: 180px;
  }
`;

/* Card body — mirrors ProductDetails */
const SkeletonCardBody = styled.div`
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;

  @media (max-width: 768px) {
    padding: 10px;
    gap: 4px;
  }
`;

/* Price row — mirrors PriceAndButtonContainer */
const SkeletonPriceRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
`;

/* Cart button circle — mirrors AddToCartButton */
const SkeletonCircle = styled(SkeletonPulse)`
  width: 36px;
  height: 36px;
  min-width: 36px;
  border-radius: 50%;
`;

/* ================================ */
/* Progressive Image Loading Styles */
/* ================================ */

const ProductImageWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  background-color: #f5f5f5;

  @media (max-width: 768px) {
    aspect-ratio: 1 / 1;
  }
`;

const ProductDetailBadge = styled.div`
  position: absolute;
  /* The deal stub owns the top-left corner (48 wide, 56 tall, flush to the
     edge), so on a discounted dish the pill drops below it. */
  top: ${(props) => (props.$belowDeal ? "64px" : "10px")};
  left: 10px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.72);
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  pointer-events: none;
  opacity: 0;
  transform: translateY(-4px);
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
  backdrop-filter: blur(8px);
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.22);

  ${ProductCard}:hover & {
    opacity: 1;
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const productImageShimmer = keyframes`
  0% { background-position: -300px 0; }
  100% { background-position: 300px 0; }
`;

const ProductImageSkeleton = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #f5f5f5 25%, #ebebeb 37%, #f5f5f5 63%);
  background-size: 600px 100%;
  animation: ${productImageShimmer} 1.4s ease-in-out infinite;
`;

const ProductImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  opacity: ${(props) => (props.isLoaded === true ? 1 : 0)};
  transition:
    opacity 0.4s ease,
    transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: scale(1.05);
  }
`;

const FallbackImageContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
`;

/* Fills the tile the way a real photograph would, so a menu that is still
   being photographed reads as a menu rather than as a page that failed. */
const FallbackImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const MenuSearchRow = styled.div`
  padding: 12px 16px 10px;
  background: #ffffff;

  @media (max-width: 768px) {
    padding: 10px 12px 8px;
  }
`;

const NoMatchMessage = styled.div`
  margin: 4px 16px 12px;
  padding: 14px 16px;
  border-radius: 10px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  color: #7c2d12;
  font-size: 15px;

  button {
    background: none;
    border: none;
    padding: 0;
    color: #f24405;
    font: inherit;
    font-weight: 600;
    text-decoration: underline;
    cursor: pointer;
  }
`;

const NoProductsMessage = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 50vh;
  font-size: 18px;
  font-style: italic;
  color: #721c24;
  text-align: center;
  background-color: #f8d7da;
  border: 1px solid #f5c2c7;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
  margin: 20px;
  transition:
    transform 0.3s,
    box-shadow 0.3s;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 768px) {
    font-size: 20px;
    padding: 15px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
    padding: 10px;
  }

  h1 {
    font-size: 1.8em;
    margin-bottom: 10px;
    color: #721c24;
  }

  p {
    font-size: 1.2em;
    margin: 0;
    color: #721c24;
  }

  svg {
    font-size: 3rem;
    color: #721c24;
    margin-bottom: 15px;
  }
`;

const StyledMenu = styled(Menu)`
  .MuiPaper-root {
    border-radius: 12px;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.18);
    overflow: hidden;
    min-width: 240px;
    border: 1px solid rgba(0, 0, 0, 0.06);
  }

  .MuiList-root {
    padding: 6px 0;
  }

  .MuiMenuItem-root {
    padding: 10px 16px;
    font-family: "Inter", sans-serif;
    font-size: 14px;
    font-weight: 500;
    transition: all 0.2s ease;
    border-left: 3px solid transparent;
    gap: 4px;

    &:hover {
      background: linear-gradient(
        90deg,
        rgba(242, 68, 5, 0.08) 0%,
        rgba(242, 68, 5, 0.02) 100%
      );
      border-left-color: #f24405;

      .MuiListItemIcon-root {
        transform: scale(1.1);
      }

      .MuiListItemText-primary {
        color: #f24405;
        font-weight: 600;
      }
    }
  }

  .MuiListItemIcon-root {
    min-width: 32px;
    transition: all 0.2s ease;
  }

  .MuiListItemText-primary {
    font-family: "Poppins", sans-serif;
    font-size: 13px;
    font-weight: 500;
    color: #2c3e50;
    transition: all 0.2s ease;
  }

  /* Style for disabled menu item (restaurant image) */
  .MuiMenuItem-root.Mui-disabled {
    opacity: 1;
    padding: 8px 12px;
    margin-bottom: 4px;
    border-bottom: 1px solid #f0f0f0;

    img {
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
    }
  }
`;
